import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  fetchProductsByBranch,
  fetchAllProductsForCategory,
  fetchPrivateProducts,
  makeProductPublic,
  type FetchProductsOptions,
  type FetchPrivateProductsOptions,
} from "../api/productApi";

export type { Product, ProductCategory, ProductBranch } from "../api/productApi";

// Query Keys
export const productKeys = {
  all: ["products"] as const,
  lists: () => [...productKeys.all, "list"] as const,
  list: (options: FetchProductsOptions) => [...productKeys.lists(), options] as const,
  byCategory: (branchId: string, categoryId: string) =>
    [...productKeys.all, "byCategory", branchId, categoryId] as const,
  privateLists: () => [...productKeys.all, "private"] as const,
  privateList: (options: FetchPrivateProductsOptions) =>
    [...productKeys.privateLists(), options] as const,
};

// Hooks
export const useProducts = (options: FetchProductsOptions) => {
  return useQuery({
    queryKey: productKeys.list(options),
    queryFn: () => fetchProductsByBranch(options),
    enabled: !!options.branchId,
  });
};

export const usePrivateProducts = (options: FetchPrivateProductsOptions) => {
  return useQuery({
    queryKey: productKeys.privateList(options),
    queryFn: () => fetchPrivateProducts(options),
    enabled: !!options.branchId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useMakeProductsPublic = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productIds: string[]) =>
      Promise.all(productIds.map((id) => makeProductPublic(id))),
    onSuccess: (_, productIds) => {
      toast.success(
        `${productIds.length} product${productIds.length > 1 ? "s" : ""} set as public`
      );
      queryClient.invalidateQueries({ queryKey: productKeys.privateLists() });
      options?.onSuccess?.();
    },
    onError: () => {
      toast.error("Failed to update some products");
    },
  });
};

export const useProductsByCategory = (branchId: string, categoryId: string) => {
  return useQuery({
    queryKey: productKeys.byCategory(branchId, categoryId),
    queryFn: () => fetchAllProductsForCategory(branchId, categoryId),
    enabled: !!branchId && !!categoryId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
